import React, { useState, useEffect } from 'react';
import GameContent from './components/GameContent';
import UserProfile from './components/UserProfile';
import MessagesPanel from './components/MessagesPanel';
import TopBar from './components/TopBar';
import FilterPanel from './components/FilterPanel';
import AdOverlay from './components/AdOverlay';
import EarnTokenModal from './components/EarnTokenModal';
import ErrorOverlay from './components/ErrorOverlay';
import BottomNavBar from './components/BottomNavBar';
import NonCriticalErrorBanner from './components/NonCriticalErrorBanner/NonCriticalErrorBanner';
import NicknameModal from './components/NicknameModal';
import { useTheme } from './context/ThemeContext';
import { useGlobalError } from './context/GlobalErrorContext';
import { useGameState } from './context/GameStateContext';
import { useAd } from './context/AdContext';
import { MetadataProvider, useMetadata } from './context/MetadataContext';
import { UserProvider, useUser } from './context/UserContext';
import { AdProvider } from './context/AdContext';
import { GameStateProvider } from './context/GameStateContext';
import { PuzzleStateProvider } from './context/PuzzleStateContext';
import RegistrationForm from './components/RegistrationForm';
import { checkRegistration, registerErrorHandlers } from './services/api';
import storage from './platform/storage';

// Component for registered users with all game contexts
function RegisteredApp({ onUnregister }) {
  const [showProfile, setShowProfile] = useState(false);
  const [showFilter, setShowFilter] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [showNicknameModal, setShowNicknameModal] = useState(false);
  const { isDarkMode, toggleTheme } = useTheme();
  const { gameData, findPuzzleByChallenge, findPuzzleByShareCode } = useGameState();
  const { currentError, triggerError, clearError } = useGlobalError();
  const { showEarnTokenModal, hideEarnTokenModal } = useAd();
  const [challengeCodeProcessed, setChallengeCodeProcessed] = useState(false);
  const [shareCodeProcessed, setShareCodeProcessed] = useState(false);

  // handlers for modals
  const handleProfileOpen = () => setShowProfile(true);
  const handleProfileClose = () => setShowProfile(false);
  const handleFilterOpen = () => setShowFilter(true);
  const handleFilterClose = () => setShowFilter(false);
  const handleMessagesOpen = () => setShowMessages(true);
  const handleMessagesClose = () => setShowMessages(false);
  const handleNicknameModalOpen = () => {
    setShowProfile(false); // Close user profile
    setShowNicknameModal(true);
  };
  const handleNicknameModalClose = () => setShowNicknameModal(false);

  // Check for challenge code in URL on app load
  useEffect(() => {
    // Process challenge code if we haven't processed it yet
    if (!challengeCodeProcessed) {
      try {
        // Get the current URL search parameters
        const urlParams = new URLSearchParams(window.location.search);
        const challengeCode = urlParams.get('challenge_code');
        
        if (challengeCode) {
          console.log('Challenge code detected:', challengeCode);
          
          // Process the challenge code
          findPuzzleByChallenge(challengeCode);
          
          // Remove the challenge_code parameter from URL without affecting browser history
          if (window.history && window.history.replaceState) {
            // Create a new URL without the challenge_code parameter
            urlParams.delete('challenge_code');
            const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
            window.history.replaceState({}, document.title, newUrl);
          }
        }
      } catch (error) {
        console.error('Error processing challenge code:', error);
        triggerError({
          type: 'non-critical',
          message: 'Failed to process challenge link',
          error
        });
      } finally {
        setChallengeCodeProcessed(true);
      }
    }
  }, [challengeCodeProcessed, findPuzzleByChallenge, triggerError]);

  // Check for share code in URL on app load
  useEffect(() => {
    // Process share code if we haven't processed it yet
    if (!shareCodeProcessed) {
      try {
        // Get the current URL search parameters
        const urlParams = new URLSearchParams(window.location.search);
        const shareCode = urlParams.get('share_code');
        
        if (shareCode) {
          console.log('Share code detected:', shareCode);
          
          // Process the share code
          findPuzzleByShareCode(shareCode);
          
          // Remove the share_code parameter from URL without affecting browser history
          if (window.history && window.history.replaceState) {
            // Create a new URL without the share_code parameter
            urlParams.delete('share_code');
            const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
            window.history.replaceState({}, document.title, newUrl);
          }
        }
      } catch (error) {
        console.error('Error processing share code:', error);
        triggerError({
          type: 'non-critical',
          message: 'Failed to process shared puzzle link',
          error
        });
      } finally {
        setShareCodeProcessed(true);
      }
    }
  }, [shareCodeProcessed, findPuzzleByShareCode, triggerError]);

  // Register error handlers for automatic error clearing on successful API responses
  useEffect(() => {
    registerErrorHandlers(triggerError, clearError);
  }, [triggerError, clearError]);
  
  // Handler for closing critical error overlay
  const handleErrorClose = () => {
    //do nothing
  };

  return (
    <>
      {/* Show critical error overlay if there's a critical error */}
      {currentError && currentError.severity === 'critical' && (
        <ErrorOverlay
          errorType={currentError.type}
          errorMessage={currentError.message}
          onRetry={currentError.canRetry ? () => window.location.reload() : undefined}
          onClose={() => clearError()}
        />
      )}

      <NonCriticalErrorBanner />
      <TopBar
        onProfileOpen={handleProfileOpen}
        onFilterOpen={handleFilterOpen}
        onMessagesOpen={handleMessagesOpen}
        isDarkMode={isDarkMode}
        onToggleTheme={toggleTheme}
        showFilter={!gameData || !gameData.state?.status}
      />
      
      <div className="main-content">
        <GameContent />
      </div>
      
      {showProfile && (
        <div className="profile-overlay" onClick={handleProfileClose}>
          <div className="profile-panel" onClick={e => e.stopPropagation()}>
            <UserProfile
              onClose={handleProfileClose}
              onOpenNicknameModal={handleNicknameModalOpen}
            />
          </div>
        </div>
      )}
      {showMessages && (
        <div className="profile-overlay" onClick={handleMessagesClose}>
          <div className="profile-panel" onClick={e => e.stopPropagation()}>
            <MessagesPanel
              isOpen={showMessages}
              onClose={handleMessagesClose}
            />
          </div>
        </div>
      )}
      <FilterPanel
        isOpen={showFilter}
        onClose={handleFilterClose}
      />
      <BottomNavBar />
      <AdOverlay />
      <EarnTokenModal
        isOpen={showEarnTokenModal}
        onClose={hideEarnTokenModal}
      />
      <NicknameModal
        isOpen={showNicknameModal}
        onClose={handleNicknameModalClose}
      />
    </>
  );
}

// Main App component that handles registration logic
function App() {
  const { criticalError, retryCount, handleRetry } = useGlobalError();
  const { hydrated: themeHydrated } = useTheme();
  const [isRegistered, setIsRegistered] = useState(null); // null = loading, true/false = determined
  const [inviteCode, setInviteCode] = useState(null);
  const [linkingParams, setLinkingParams] = useState(null);

  // Check for invite code and linking params in URL before registration check
  useEffect(() => {
    try {
      const urlParams = new URLSearchParams(window.location.search);

      // Check for linking parameters
      const userKey = urlParams.get('user_key');
      const linkingCode = urlParams.get('linking_code');

      if (userKey && linkingCode) {
        console.log('Linking parameters detected:', { userKey, linkingCode });
        setLinkingParams({ userKey, linkingCode });

        // Remove linking parameters from URL
        if (window.history && window.history.replaceState) {
          urlParams.delete('user_key');
          urlParams.delete('linking_code');
          const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
          window.history.replaceState({}, document.title, newUrl);
        }
      }

      // Check for invite code
      const code = urlParams.get('invite_code');

      if (code) {
        console.log('Invite code detected:', code);
        // Store invite code in storage
        storage.setItem('inviteCode', code);
        setInviteCode(code);

        // Remove invite_code parameter from URL
        if (window.history && window.history.replaceState) {
          urlParams.delete('invite_code');
          const newUrl = window.location.pathname + (urlParams.toString() ? `?${urlParams.toString()}` : '');
          window.history.replaceState({}, document.title, newUrl);
        }
      } else {
        // Check if there's an invite code in storage
        storage.getItem('inviteCode').then(storedInviteCode => {
          if (storedInviteCode) {
            setInviteCode(storedInviteCode);
          }
        });
      }
    } catch (error) {
      console.error('Error processing URL parameters:', error);
    }
  }, []);

  // Check registration status once on mount and store in state
  useEffect(() => {
    // Only check once on initial mount
    if (isRegistered === null) {
      let cancelled = false;
      const checkStoredRegistration = async () => {
        const userKey = await storage.getItem('userKey');
        const deviceUid = await storage.getItem('deviceUid');
        if (!cancelled) {
          setIsRegistered(Boolean(userKey && deviceUid));
        }
      };
      checkStoredRegistration();
      return () => { cancelled = true; };
    }
  }, [isRegistered]);

  // Handler for successful registration
  const handleRegistered = () => {
    setIsRegistered(true);
  };

  // Handler for closing critical error overlay
  const handleErrorClose = () => {
    //do nothing
  };

  return (
    <div className="app">
      {/* Show critical error overlay if there's a critical error */}
      {criticalError && (
        <ErrorOverlay
          errorType={criticalError.type}
          errorMessage={criticalError.message}
          onRetry={retryCount < 3 ? handleRetry : undefined}
          onClose={handleErrorClose}
          retryCount={retryCount}
          retryAfter={criticalError.retryAfter}
        />
      )}

      {isRegistered === null || !themeHydrated ? (
        // Show loading state while checking registration and loading the stored theme
        <div className="loading">Checking registration status...</div>
      ) : !isRegistered ? (
        // Not registered - show only registration form
        <div className="registration-wrapper">
          <RegistrationForm
            onRegistered={handleRegistered}
            inviteCode={inviteCode}
            linkingParams={linkingParams}
          />
        </div>
      ) : (
        // Registered - show registered app with contexts
        <RegisteredAppWithContexts />
      )}
    </div>
  );
}

function AppWithProviders() {
  return (
    <App />
  );
}

// Holds rendering until every context has loaded its stored values
function HydrationGate({ children }) {
  const { hydrated: metadataHydrated } = useMetadata();
  const { hydrated: userHydrated } = useUser();
  const { hydrated: adHydrated } = useAd();
  const { hydrated: gameStateHydrated } = useGameState();

  if (!(metadataHydrated && userHydrated && adHydrated && gameStateHydrated)) {
    return <div className="loading">Loading...</div>;
  }
  return children;
}

// Wrapper component that provides game-specific contexts only for registered users
function RegisteredAppWithContexts() {
  return (
    <MetadataProvider>
      <UserProvider>
        <AdProvider>
          <GameStateProvider>
            <PuzzleStateProvider>
              <HydrationGate>
                <RegisteredApp />
              </HydrationGate>
            </PuzzleStateProvider>
          </GameStateProvider>
        </AdProvider>
      </UserProvider>
    </MetadataProvider>
  );
}

export default AppWithProviders;
